// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import {Logger} from '@app/features/platform/utils/AppLogger';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {roomViewFor} from '@app/features/voice_scene/state/RoomView';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {effectiveRoomTheme} from '@app/features/voice_scene/utils/EffectiveRoomTheme';
import {roomFileUrl} from '@app/features/voice_scene/utils/RoomFileUrl';
import {effectiveQualityTier, roomFileForTier, roomQualityProfile} from '@app/features/voice_scene/utils/RoomQuality';
import {getWebglSupport} from '@app/features/voice_scene/utils/WebglSupport';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import {reaction} from 'mobx';

const logger = new Logger('RoomPrefetchCommands');

// A room opens only once its call connects, so a join starts the 3D chunk and the room file download meanwhile. The
// file is read into the HTTP cache, where the renderer's own fetch finds it; its bytes are not kept here. It loads at
// low priority, and stops if the join ends before it is done.
export function prefetchVoiceRoomForJoin(channel: Channel): void {
	const theme = effectiveRoomTheme(
		channel.roomTheme,
		readVoiceRoomAssignment(ExperimentAssignments.response).default_room_theme,
		channel.type,
	);
	if (!theme || !roomViewFor(channel).show) return;
	void import('@app/features/voice_scene/scene/VoiceSceneRenderer').catch((error: unknown) => {
		logger.warn('3D room chunk prefetch failed', error);
	});
	const controller = new AbortController();
	const stopWatching = reaction(
		() => MediaEngine.channelId === channel.id && (MediaEngine.connecting || MediaEngine.connected),
		(joining) => {
			if (!joining) controller.abort();
		},
	);
	const profile = roomQualityProfile(
		effectiveQualityTier(
			{quality: VoiceSceneSettings.effectiveQuality, autoQualityCap: VoiceSceneSettings.autoQualityCap},
			getWebglSupport(),
		),
	);
	fetch(roomFileForTier(roomFileUrl(theme), profile), {priority: 'low', signal: controller.signal})
		.then((response) => response.body?.pipeTo(new WritableStream(), {signal: controller.signal}))
		.catch((error: unknown) => {
			logger.debug('3D room file prefetch stopped', error);
		})
		.finally(stopWatching);
}
